import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { QuestionMarkCircleIcon } from '@heroicons/react/16/solid';

const TOOLTIP_MAX_W = 288; // max-w-xs ≈ 18rem
const GAP = 6;
const MARGIN = 8;

/**
 * A muted (?) icon that shows a tooltip with `text` on hover or keyboard focus.
 * Rendered via createPortal so it escapes Card's overflow-hidden without clipping.
 */
export function HelpTip({ text, className }: { text: string; className?: string }) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const ref = useRef<HTMLSpanElement>(null);

  const show = () => {
    if (ref.current) setAnchor(ref.current.getBoundingClientRect());
  };
  const hide = () => setAnchor(null);

  // Position tooltip below the icon, right-edge aligned to icon's right edge.
  // Clamp so it stays within viewport by MARGIN px on all sides.
  const left = anchor
    ? Math.max(MARGIN, Math.min(anchor.right - TOOLTIP_MAX_W, window.innerWidth - TOOLTIP_MAX_W - MARGIN))
    : 0;
  const top = anchor ? anchor.bottom + GAP : 0;

  return (
    <span
      ref={ref}
      role="button"
      tabIndex={0}
      aria-label={text}
      className={clsx(
        'inline-block align-middle cursor-help',
        'text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-sm',
        className,
      )}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      <QuestionMarkCircleIcon className="size-3.5" />
      {anchor !== null &&
        createPortal(
          <div
            className="pointer-events-none fixed z-50 rounded-md border border-gray-200 bg-white p-2 text-xs leading-snug text-gray-600 shadow-lg dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
            style={{ top, left, maxWidth: TOOLTIP_MAX_W }}
          >
            {text}
          </div>,
          document.body,
        )}
    </span>
  );
}
